import { Pages, PagesEnum } from '../pages'

import { observable, runInAction } from 'mobx'
import { openInNewTab } from '../utilities/openInNewTab'
import { NavigateFunction, Location, RouteObject } from 'react-router-dom'
import { urlService } from './url.service'

class NavigationService {
    // Routes storage
    private routes: RouteObject[] = []
    setRoutes(r: RouteObject[]) {
        this.routes = r
    }
    getRoutes(): RouteObject[] {
        return this.routes
    }

    private navigate: NavigateFunction
    setNavigate(navigate: NavigateFunction) {
        this.navigate = navigate
        window.navigate = navigate
    }

    private location: Location
    setLocation(navigate: Location) {
        this.location = navigate
    }
    getLocation() {
        return this.location
    }

    pageObs = observable.box(null as PagesEnum)

    setCurrentPage(page: PagesEnum) {
        runInAction(() => {
            this.pageObs.set(page)
        })

        return true
    }

    getCurrentPage = () => this.pageObs.get()

    goToPage(page: PagesEnum, params?: { [key: string]: string }, keepQueryParams = false) {
        setTimeout(() => {
            let url = this.getPageUrl(page, params, keepQueryParams)
            this.navigate(url)
        }, 10)
    }

    getPageOrSubpage(page: PagesEnum, subPage?: string) {
        let pageOrSubpage = Pages[page]
        if (!!subPage && subPage.length) {
            const subPageElement = pageOrSubpage?.subPages?.find(subP => subP?.alias?.toString() === subPage)
            if (!!subPageElement && subPageElement.urls && subPageElement.urls.length && !!subPageElement.urls[0]) {
                pageOrSubpage = subPageElement
            }
        }

        return pageOrSubpage
    }

    // If the params provided match any url params, they will be replaced
    getPageUrl(page: PagesEnum, params?: { [key: string]: string }, keepQueryParams = false, subPage?: string) {
        const pageOrSubpage = this.getPageOrSubpage(page, subPage)

        /*
         * The "urls" property is undefined if a react router page does not have a corresponding umbraco page setup.
         * Hence, if this is the case return an empty string to prevent UI from breaking.
         * */
        let url = pageOrSubpage.urls && pageOrSubpage.urls.length ? pageOrSubpage.urls[0] : ''
        if (!url) return url

        if (keepQueryParams) {
            const query = new URLSearchParams(this.location.search)
            query.forEach((value, key) => {
                if (!params) params = {}
                params[key] = value
            })
        }

        if (params) {
            let index = 0
            for (const key in params) {
                if (pageOrSubpage.params && pageOrSubpage.params.includes(key)) {
                    url = url.replace(':' + key, params[key])
                } else {
                    url += index == 0 ? '?' : '&'
                    url += `${key}=${params[key]}`
                    index++
                }
            }
        }

        // if the url does not contain a trailing slash add one
        if (!url.endsWith('/')) {
            url += '/'
        }

        return url
    }

    getQueryParams(params: { [key: string]: string }) {
        let url = ''

        if (params) {
            let index = 0
            for (let key in params) {
                url += index == 0 ? '?' : '&'
                url += `${key}=${params[key]}`
                index++
            }
        }

        return url
    }

    goTo(url: string, newTab: boolean = false) {
        if (this.isRouted(url)) {
            if (newTab) {
                const origin: string = url.slice(0, 0) === '/' ? document.location.origin : ''
                openInNewTab(origin + url)
            } else {
                const routedUrl = urlService.getRoutedUrl(url)
                setTimeout(() => {
                    this.navigate(routedUrl)
                }, 10)
            }
        } else {
            if (newTab) {
                if (url.startsWith(window.origin)) {
                    openInNewTab(`${document.location.origin}/${url}`)
                    return
                }
                openInNewTab(`${url}`)
            } else {
                if (url.startsWith(window.origin)) {
                    document.location.assign(`/${url}`)
                    return
                }
                document.location.assign(`${url}`)
            }
        }
    }

    goToHome() {
        this.goTo('/')
    }

    private navigationInProgress = false
    canNavigate() {
        return !this.navigationInProgress
    }

    goBack() {
        if (!this.navigationInProgress) {
            this.navigationInProgress = true
            this.navigate(-1)
        }
    }

    isRouted(url: string) {
        return url.startsWith('/') || url.startsWith(document.location.origin)
    }

    private previousPage: string
    setPreviousPage(page: string) {
        this.previousPage = page
    }
    getPreviousPage() {
        return this.previousPage
    }
}

export const navigationService = new NavigationService()
